// The AI Champions page's content, kept in four SharePoint lists on the
// AISupportBot site (created by scripts/sharepoint/create-champion-lists.js):
// Champion Communities, Champion Roster, Champion Milestones and Champion
// Settings. Champion photos live in Shared Documents/Champions Photos.
import { logActivity } from "./activityLog";
import { createListItem, deleteListItem, GRAPH_BASE, SITE_ID, updateListItem } from "./graph";
import { uploadToFolder } from "./featured";
import {
  CHAMPIONS,
  DEFAULT_CONFIG,
  type Champion,
  type ChampionsConfig,
  type Community,
  type CommunityId,
  type Milestone,
} from "../data/championsDirectory";

const env = import.meta.env;
export const CHAMPION_LISTS = {
  communities: env.VITE_SP_CHAMPION_COMMUNITIES_LIST_ID ?? "a9110cc3-97e8-41a3-b3c0-656da560a356",
  roster: env.VITE_SP_CHAMPION_ROSTER_LIST_ID ?? "ab098dad-f239-4e77-8c7d-0b8ee298c904",
  milestones: env.VITE_SP_CHAMPION_MILESTONES_LIST_ID ?? "1d5a98f5-0da8-418f-b3c5-4b3af792ce28",
  settings: env.VITE_SP_CHAMPION_SETTINGS_LIST_ID ?? "b023f498-2c44-49b6-870a-08f38d704367",
};
export const CHAMPION_PHOTO_FOLDER = "Champions Photos";

/** The page's content plus the bits needed to save changes back. */
export type LoadedChampions = {
  config: ChampionsConfig;
  /** Champion Settings row ids, by setting name. */
  settingIds: Record<string, string>;
  /** Whether Champion Milestones has its Community column yet (added by
   *  re-running create-champion-lists.js). Without it, every milestone is
   *  on every community's timeline. */
  milestoneCommunities: boolean;
};

type Row = { id: string; fields: Record<string, unknown> };

const text = (v: unknown) => (typeof v === "string" ? v.trim() : v == null ? "" : String(v));
const link = (v: unknown) => (v && typeof v === "object" && "Url" in v ? text((v as { Url?: unknown }).Url) : text(v));
const active = (r: Row) => r.fields.Active !== false;
const byOrder = (a: Row, b: Row) =>
  (Number(a.fields.SortOrder) || Infinity) - (Number(b.fields.SortOrder) || Infinity) || Number(a.id) - Number(b.id);

async function hasColumn(token: string, listId: string, name: string): Promise<boolean> {
  const res = await fetch(`${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/columns?$select=name`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return false;
  return ((await res.json()) as { value: { name: string }[] }).value.some((c) => c.name === name);
}

async function rows(token: string, listId: string): Promise<Row[]> {
  const out: Row[] = [];
  let url: string | undefined = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items?expand=fields&$top=200`;
  while (url) {
    const res: Response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Couldn't load the AI Champions lists (${res.status}).`);
    const data = (await res.json()) as { value: Row[]; "@odata.nextLink"?: string };
    out.push(...data.value.map((r) => ({ id: r.id, fields: r.fields ?? {} })));
    url = data["@odata.nextLink"];
  }
  return out;
}

const SETTING = { headline: "Headline", lede: "Introduction", surveyUrl: "SurveyLink" } as const;
const EXCLUDE_SETTING = "LeaderboardExclude";
const splitList = (v: string) =>
  v
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

export async function fetchChampions(token: string): Promise<LoadedChampions> {
  const [commRows, rosterRows, msRows, settingRows, milestoneCommunities] = await Promise.all([
    rows(token, CHAMPION_LISTS.communities),
    rows(token, CHAMPION_LISTS.roster),
    rows(token, CHAMPION_LISTS.milestones),
    rows(token, CHAMPION_LISTS.settings),
    hasColumn(token, CHAMPION_LISTS.milestones, "Community"),
  ]);

  const communities: Community[] = commRows
    .filter(active)
    .sort(byOrder)
    .map((r) => ({
      id: (text(r.fields.CommunityKey) || `c${r.id}`) as CommunityId,
      itemId: r.id,
      name: text(r.fields.Title),
      examples: text(r.fields.Examples)
        .split(/\r?\n/)
        .map((x) => x.trim())
        .filter(Boolean),
    }));
  const keyByItem = Object.fromEntries(communities.map((c) => [c.itemId, c.id]));

  const roster = rosterRows.filter(active).sort(byOrder);
  const champions: Champion[] = roster.map((r) => ({
    id: r.id,
    name: text(r.fields.Title),
    community: keyByItem[text(r.fields.CommunityLookupId)] ?? communities[0]?.id ?? "land",
    area: text(r.fields.Area),
    title: text(r.fields.JobTitle) || undefined,
    location: text(r.fields.WorkLocation) || undefined,
    email: text(r.fields.EmailAddress) || undefined,
    photo: link(r.fields.PhotoUrl) || undefined,
  }));

  const milestones: Milestone[] = msRows
    .filter(active)
    .map((r) => ({
      id: r.id,
      text: text(r.fields.Title),
      date: text(r.fields.MilestoneDate).slice(0, 10),
      community: keyByItem[text(r.fields.CommunityLookupId)],
    }))
    .filter((m) => m.date)
    .sort((a, b) => a.date.localeCompare(b.date));

  const settings = Object.fromEntries(settingRows.map((r) => [text(r.fields.Title), text(r.fields.Value)]));
  const settingIds = Object.fromEntries(settingRows.map((r) => [text(r.fields.Title), r.id]));

  return {
    settingIds,
    milestoneCommunities,
    config: {
      headline: settings[SETTING.headline] || DEFAULT_CONFIG.headline,
      lede: settings[SETTING.lede] || DEFAULT_CONFIG.lede,
      surveyUrl: settings[SETTING.surveyUrl] || DEFAULT_CONFIG.surveyUrl,
      leaderboardExclude: splitList(settings[EXCLUDE_SETTING] ?? ""),
      leads: roster.filter((r) => r.fields.ProgramLead === true).map((r) => text(r.fields.Title)),
      communities: communities.length ? communities : DEFAULT_CONFIG.communities,
      champions,
      milestones,
    },
  };
}

/** A photo bundled with the app for this person, when SharePoint has none. */
export function bundledPhoto(name: string): string | undefined {
  return CHAMPIONS.find((c) => c.name.toLowerCase() === name.toLowerCase())?.photo;
}

// ---------- Saving ----------

const linkField = (url: string | undefined, name: string) => (url ? { Url: url, Description: name } : null);
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Photos picked in the admin panel but not uploaded yet, by their preview URL. */
export type PendingPhotos = Map<string, File>;

/**
 * Writes the difference between what was loaded and the edited version:
 * new rows are added, changed rows updated and removed rows deleted.
 * Photos picked in the admin panel are uploaded first.
 */
export async function saveChampions(
  token: string,
  before: LoadedChampions,
  after: ChampionsConfig,
  photos: PendingPhotos,
  onStep?: (label: string) => void
): Promise<void> {
  const L = CHAMPION_LISTS;
  const was = before.config;

  // Communities: names and examples (the set of communities is fixed here).
  for (const c of after.communities) {
    const old = was.communities.find((x) => x.itemId === c.itemId);
    if (c.itemId && old && (old.name !== c.name || !same(old.examples, c.examples))) {
      onStep?.(`Saving ${c.name}`);
      await updateListItem(token, L.communities, c.itemId, { Title: c.name, Examples: c.examples.join("\n") });
    }
  }
  const commItem = Object.fromEntries(after.communities.map((c) => [c.id, c.itemId]));

  // Champions.
  const oldById = new Map(was.champions.filter((c) => c.id).map((c) => [c.id!, c]));
  for (const [i, c] of after.champions.entries()) {
    let photo = c.photo;
    const file = photo ? photos.get(photo) : undefined;
    if (file) {
      onStep?.(`Uploading ${c.name}'s photo`);
      const ext = file.name.split(".").pop() || "jpg";
      photo = (await uploadToFolder(token, CHAMPION_PHOTO_FOLDER, file, `${c.name}.${ext}`)).webUrl;
    }
    const lead = after.leads.includes(c.name);
    const fields = {
      Title: c.name,
      CommunityLookupId: commItem[c.community],
      Area: c.area,
      JobTitle: c.title ?? "",
      WorkLocation: c.location ?? "",
      EmailAddress: c.email ?? "",
      ProgramLead: lead,
    };
    const old = c.id ? oldById.get(c.id) : undefined;
    if (!c.id) {
      onStep?.(`Adding ${c.name}`);
      const id = (await createListItem(token, L.roster, { ...fields, SortOrder: i + 1, Active: true })).id;
      // Hyperlink columns are rejected on create, so the photo follows.
      if (photo) await updateListItem(token, L.roster, id, { PhotoUrl: linkField(photo, c.name) });
    } else if (old) {
      const oldFields = { ...fields, Title: old.name, CommunityLookupId: commItem[old.community], Area: old.area, JobTitle: old.title ?? "", WorkLocation: old.location ?? "", EmailAddress: old.email ?? "", ProgramLead: was.leads.includes(old.name) };
      const changed = !same(fields, oldFields) || photo !== old.photo;
      if (changed) {
        onStep?.(`Saving ${c.name}`);
        await updateListItem(token, L.roster, c.id, { ...fields, PhotoUrl: linkField(photo, c.name) });
      }
    }
  }
  for (const old of was.champions) {
    if (old.id && !after.champions.some((c) => c.id === old.id)) {
      onStep?.(`Removing ${old.name}`);
      await deleteListItem(token, L.roster, old.id);
    }
  }

  // Milestones.
  const oldMs = new Map(was.milestones.filter((m) => m.id).map((m) => [m.id!, m]));
  for (const m of after.milestones) {
    if (!m.text.trim() || !m.date) continue;
    const fields: Record<string, unknown> = { Title: m.text.trim(), MilestoneDate: `${m.date}T12:00:00Z` };
    if (before.milestoneCommunities) fields.CommunityLookupId = m.community ? commItem[m.community] ?? null : null;
    if (!m.id) await createListItem(token, L.milestones, { ...fields, Active: true });
    else if (!same(oldMs.get(m.id), m)) await updateListItem(token, L.milestones, m.id, fields);
  }
  for (const old of was.milestones) {
    if (old.id && !after.milestones.some((m) => m.id === old.id)) await deleteListItem(token, L.milestones, old.id);
  }

  // Settings.
  for (const [key, name] of Object.entries(SETTING) as [keyof typeof SETTING, string][]) {
    if (after[key] === was[key]) continue;
    const id = before.settingIds[name];
    if (id) await updateListItem(token, L.settings, id, { Value: after[key] });
    else await createListItem(token, L.settings, { Title: name, Value: after[key] });
  }
  const exclude = after.leaderboardExclude.map((x) => x.trim()).filter(Boolean);
  if (!same(exclude, was.leaderboardExclude)) {
    const id = before.settingIds[EXCLUDE_SETTING];
    if (id) await updateListItem(token, L.settings, id, { Value: exclude.join("\n") });
    else await createListItem(token, L.settings, { Title: EXCLUDE_SETTING, Value: exclude.join("\n") });
  }

  void logActivity("Feature", "AI Champions page updated", `${after.champions.length} champions`, "champions");
}
